<script setup lang="ts">
import { ref } from "vue";
import Modal from "@/components/modals/ModalContainer.vue";
import { useStoreUserV2 } from "~/stores/storeUserV2";
import { auctionApi } from "~/api/auction";

import { useI18n } from "vue-i18n";
import { useStoreApp } from "~/stores/storeApp";
import { useWeb3AuthStore } from "~/stores/storeWeb3auth";

const storeUser = useStoreUserV2();
const storeApp = useStoreApp();
const web3AuthStore = useWeb3AuthStore();

const { locale } = useI18n();

const emit = defineEmits(["onClose"]);

const handleOnClose = () => {
  if (loading.value) return;
  if (web3AuthStore.smartWalletAddress) {
    storeUser.smartWalletAddress = web3AuthStore.smartWalletAddress;
  }
  emit("onClose");
};

const loading = ref(false);
const smartWalletActivated = ref(false);

const migrateToUSDC = async () => {
  if (loading.value) return;

  try {
    loading.value = true;

    const resp = await auctionApi.users.switchToSmartWallet.mutate();
    // console.log("Migration to Smart Wallet response:", resp);

    // Waiting 2s
    await new Promise((resolve) => setTimeout(resolve, 2000));

    smartWalletActivated.value = true;
  } catch (error) {
    console.error("Error toggling wallet currency migration:", error);
  } finally {
    loading.value = false;
  }
};

const migrateUsdc = async () => {
  storeApp.showMigrateUsdcToSmartWalletModal = true;
  // handleOnClose();
};
</script>

<template>
  <Modal :show="true" headerIMG="/images/fungigall_smart.jpg" :showCloseButton="false">
    <template v-if="locale === 'en'">
      <div v-if="smartWalletActivated" class="flex flex-col gap-2 text-sm">
        <div class="text-2xl text-center font-semibold mb-4">Evolution Successfully Completed!</div>
        <div class="bg-neutral-800 rounded-lg p-4 flex-col gap-2 mb-4 mt-2">
          <span class="mb-1">
            📍 <span class="text-orange-40">New Deposit Address</span>: Your evolved account now has
            a unique secured address. This is where you will receive your future winnings and
            deposit your funds.
          </span>
        </div>

        <div
          v-if="storeUser.usdcRemaining > 0.5"
          class="bg-neutral-800 rounded-lg p-4 flex-col gap-2 mb-4"
        >
          <div>
            <span class="mb-2">
              💰 <span class="text-orange-40">Balance Transfer</span>: We have detected USDC on your
              old access. Would you like to transfer them to your new account to start playing
              fee-free?
            </span>
          </div>
          <Button type="primary-blue" size="xs" class="mt-4 w-full" @click="migrateUsdc"
            >Transfer my USDC</Button
          >
        </div>

        <Button type="secondary" size="xs" class="mt-2" @click="handleOnClose">Close</Button>
      </div>
      <div v-else class="flex flex-col gap-2 text-sm">
        <div class="text-2xl text-center font-semibold mb-4">Activate the Ultra Experience!</div>
        <span class="mb-2"
          >Your manager experience is moving to the next level. Your account now benefits from
          advanced technology to simplify your actions:</span
        >
        <span class="mb-2">
          🛡️ <span class="text-orange-40">Total Transparency</span>: You only pay the exact price of
          your cards. Network fees disappear; Fungiball takes care of everything.
        </span>

        <span class="mb-2">
          📦 <span class="text-orange-40">New Capabilities</span>: This evolution enables future
          exclusive features, such as bulk market purchases and the automation of your trading and
          bidding strategies.
        </span>

        <span class="mb-2">
          🔑 <span class="text-orange-40">Full Ownership</span>: You remain the sole owner of your
          cards and funds. Fungiball has no access to your assets; you maintain total control.
        </span>

        <span>
          The evolution of your account is immediate and does not affect the security of your
          assets.
        </span>

        <Button @click="migrateToUSDC" :loading="loading" type="primary-blue" class="mt-4"
          >Activate Evolution</Button
        >
      </div>
    </template>
    <template v-else-if="locale === 'fr'">
      <div v-if="smartWalletActivated" class="flex flex-col gap-2 text-sm">
        <div class="text-2xl text-center font-semibold mb-4">Évolution terminée avec succès !</div>
        <div class="bg-neutral-800 rounded-lg p-4 flex-col gap-2 mb-4 mt-2">
          <span class="mb-1">
            📍 <span class="text-orange-40">Nouvelle adresse de dépôt</span> : Ton compte évolué
            possède désormais une adresse unique sécurisée. C'est ici que tu recevras tes futurs
            gains et déposeras tes fonds.
          </span>
        </div>

        <div
          v-if="storeUser.usdcRemaining > 0.5"
          class="bg-neutral-800 rounded-lg p-4 flex-col gap-2 mb-4"
        >
          <div>
            <span class="mb-2">
              💰 <span class="text-orange-40">Transfert de solde</span> : Nous avons détecté des
              USDC sur ton ancien accès. Veux-tu les transférer vers ton nouveau compte pour
              commencer à jouer sans frais ?
            </span>
          </div>
          <Button type="primary-blue" size="xs" class="mt-4 w-full" @click="migrateUsdc"
            >Transférez mes USDC</Button
          >
        </div>

        <Button type="secondary" size="xs" class="mt-2" @click="handleOnClose">Fermer</Button>
      </div>

      <div v-else class="flex flex-col gap-2 text-sm">
        <div class="text-2xl text-center font-semibold mb-4">Activez l'Expérience Ultra !</div>
        <span class="mb-2"
          >Votre expérience de manager passe au niveau supérieur. Votre compte bénéficie désormais
          d'une technologie avancée pour simplifier vos actions :</span
        >
        <span class="mb-2">
          🛡️ <span class="text-orange-40">Transparence Totale</span> : Vous ne payez que le prix
          exact de vos cartes. Les frais de réseau disparaissent, Fungiball s'occupe de tout.
        </span>

        <span class="mb-2">
          📦 <span class="text-orange-40">Nouvelles Capacités</span> : Cette évolution active de
          futures fonctionnalités exclusives, comme les achats groupés sur le marché et
          l'automatisation de vos stratégies de trading et d'enchères.
        </span>

        <span class="mb-2">
          🔑 <span class="text-orange-40">Pleine Propriété</span> : Vous restez le seul et unique
          propriétaire de vos cartes et de vos fonds. Fungiball n'a aucun accès à vos actifs, vous
          gardez le contrôle total.
        </span>

        <span>
          L'évolution de votre compte est immédiate et ne change rien à la sécurité de vos actifs.
        </span>

        <Button @click="migrateToUSDC" :loading="loading" type="primary-blue" class="mt-4"
          >Activer l'évolution</Button
        >
      </div>
    </template>
  </Modal>
</template>
